import { useEffect, useState } from "react";
import { useNotification } from "@hooks/useNotifications";
import { useEditorContext } from "features/editor/context/editor";
import { workspacesService } from "features/workspaces/services/workspaces-service";
import { useNavigate } from "react-router-dom";
import type { IEditorFetchParams } from "features/workspaces/types";

export const useFetchTranslationItems = (
  fetchParams: IEditorFetchParams,
  { setApprovedCount }: { setApprovedCount: (count: number) => void },
): {
  totalCount: number;
  isLoading: boolean;
  isoCodeTranslation: string | undefined;
  validationTemplateId: number | undefined;
} => {
  const { addListItems, resetListItems } = useEditorContext();
  const [validationTemplateId, setValidationTemplateId] = useState<number>();
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingApproved, setIsLoadingApproved] = useState<boolean>(false);
  const [isoCodeTranslation, setIsoCodeTranslation] = useState<string>();
  const navigate = useNavigate();

  const { showError } = useNotification();

  useEffect(() => {
    const abortController = new AbortController();

    async function fetchData() {
      setIsLoading(true);

      try {
        const response = await workspacesService.getTranslationItems(fetchParams, abortController);

        if (!abortController.signal.aborted && Array.isArray(response?.list)) {
          addListItems(response);

          setTotalCount(response?.totalCount);
          setIsoCodeTranslation(response?.translationLanguageIsoCode);
          setValidationTemplateId(response?.validationTemplateId);
        }
      } catch (error) {
        if (!abortController?.signal.aborted) {
          console.error(error);

          if (error?.response?.status === 404) {
            navigate("/");
            showError("File not found.");
          } else {
            showError("Failed to fetch translation items.");
          }

          resetListItems();
          setApprovedCount(0);
          setTotalCount(0);
        }
      }

      setIsLoading(false);
    }

    fetchData();

    return () => {
      abortController.abort();
    };
  }, [fetchParams]);

  useEffect(() => {
    const abortController = new AbortController();

    async function fetchData() {
      setIsLoadingApproved(true);

      try {
        const value = await workspacesService.getApprovedTranslationItemsCount(fetchParams, abortController);
        setApprovedCount(value);
      } catch (error) {
        console.error(error);
        setApprovedCount(0);
      }

      setIsLoadingApproved(false);
    }

    fetchData();

    return () => {
      abortController.abort();
    };
  }, [fetchParams]);

  return {
    totalCount,
    isLoading: isLoading || isLoadingApproved,
    isoCodeTranslation,
    validationTemplateId,
  };
};
